// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * Which of a patient's numbers to call.
 *
 * Shown only when the chart holds more than one dialable number. The calling
 * bot refuses to pick for itself, and the reason is worth stating: a mobile
 * marked old may have been reassigned to a stranger, so dialling the wrong one
 * is a disclosure rather than a wasted minute. The numbers arrive already
 * ranked — mobile above home above work, anything marked old last.
 */

import type { JSX } from 'react';
import { Button, Modal, Radio, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { formatForDisplay } from '@carepoint/telephony-core';

export interface NumberChoice {
  readonly e164: string;
  readonly use?: string;
}

export function ChooseNumberModal({
  opened,
  patientName,
  choices,
  onCancel,
  onChoose,
}: {
  readonly opened: boolean;
  readonly patientName: string;
  readonly choices: readonly NumberChoice[];
  readonly onCancel: () => void;
  readonly onChoose: (e164: string) => void;
}): JSX.Element {
  const [selected, setSelected] = useState<string>();
  // Pre-select the highest-ranked number rather than leaving the form empty.
  const value = selected ?? choices[0]?.e164;

  return (
    <Modal opened={opened} onClose={onCancel} title={`Call ${patientName}`} centered size="sm">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          This chart has more than one number. Choose which to call.
        </Text>
        <Radio.Group value={value} onChange={setSelected}>
          <Stack gap="xs">
            {choices.map((choice) => (
              <Radio
                key={choice.e164}
                value={choice.e164}
                label={formatForDisplay(choice.e164)}
                description={choice.use === 'old' ? 'Marked old on the chart' : choice.use}
              />
            ))}
          </Stack>
        </Radio.Group>
        <Button
          onClick={() => {
            if (value) {
              onChoose(value);
            }
          }}
          disabled={!value}
        >
          Call
        </Button>
      </Stack>
    </Modal>
  );
}
